import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { apiURL } from "../../../config/default";
import { useDispatch } from "react-redux";
import { deleteCoupon as deleteCouponAction } from "../../../actions/coupon";
import CustomeTableBtn from "../../../components/custome-table-button/CustomeTableBtn";
import SearchFilter from "../../../components/CustomeFilter/SearchFilter";
import EditTabelDrawer from "../../../components/Edit-tabel-drawer/EditTabelDrawer";
import AddNewCoupon from "../coupon/components/AddNewCoupon";
import SweetAlert from "react-bootstrap-sweetalert";
import TopBarProgress from "react-topbar-progress-indicator";
import moment from "moment";
import {
  useGetVendorPricingScheduleQuery,
  useSaveVendorPricingScheduleMutation,
  useSaveVendorPricingSlotMutation,
} from "../../../reducers/adminApi";

// Pricing Option presets matching design visual exact layout
const PRICING_OPTIONS = [
  {
    key: "standardCoupon_show",
    pricingType: "standardCoupon",
    showCoupon: true,
    title: "Standard Coupon",
    subtitle: "Show",
    bg: "#10B981",
    color: "#FFFFFF",
    chevronColor: "#FFFFFF",
  },
  {
    key: "standardCoupon_hide",
    pricingType: "standardCoupon",
    showCoupon: false,
    title: "Standard Coupon",
    subtitle: "Hide",
    bg: "#D1FAE5",
    color: "#047857",
    chevronColor: "#047857",
  },
  {
    key: "concealedCoupon_show",
    pricingType: "concealedCoupon",
    showCoupon: true,
    title: "Concealed Coupon",
    subtitle: "Show",
    bg: "#F59E0B",
    color: "#FFFFFF",
    chevronColor: "#FFFFFF",
  },
  {
    key: "concealedCoupon_hide",
    pricingType: "concealedCoupon",
    showCoupon: false,
    title: "Concealed Coupon",
    subtitle: "Hide",
    bg: "#FEF3C7",
    color: "#D97706",
    chevronColor: "#D97706",
  },
  {
    key: "quote_show",
    pricingType: "quote",
    showCoupon: true,
    title: "Quote",
    subtitle: "Show",
    bg: "#2563EB",
    color: "#FFFFFF",
    chevronColor: "#FFFFFF",
  },
  {
    key: "quote_hide",
    pricingType: "quote",
    showCoupon: false,
    title: "Quote",
    subtitle: "Hide",
    bg: "#DBEAFE",
    color: "#1D4ED8",
    chevronColor: "#1D4ED8",
  },
  {
    key: "break",
    pricingType: "break",
    showCoupon: false,
    title: "Break Map",
    subtitle: "",
    bg: "#FF5733",
    color: "#FFFFFF",
    chevronColor: "#FFFFFF",
  },
  {
    key: "smart",
    pricingType: "smart",
    showCoupon: false,
    title: "Smart Map",
    subtitle: "",
    bg: "#CCCCCC",
    color: "#111827",
    chevronColor: "#111827",
  },
  {
    key: "eliteCashback",
    pricingType: "eliteCashback",
    showCoupon: false,
    title: "Elite Earnings",
    subtitle: "",
    bg: "#E9D5FF",
    color: "#7E22CE",
    chevronColor: "#7E22CE",
  },
];

const getMatchingOption = (pricingType, showCoupon) => {
  const type = pricingType || "smart";
  const show = Boolean(showCoupon);

  if (type === "standardCoupon" || type === "coupon" || type === "flashCoupon") {
    return show ? PRICING_OPTIONS[0] : PRICING_OPTIONS[1];
  }
  if (type === "concealedCoupon") {
    return show ? PRICING_OPTIONS[2] : PRICING_OPTIONS[3];
  }
  if (type === "quote") {
    return show ? PRICING_OPTIONS[4] : PRICING_OPTIONS[5];
  }
  if (type === "break") return PRICING_OPTIONS[6];
  if (type === "smart") return PRICING_OPTIONS[7];
  if (type === "eliteCashback") return PRICING_OPTIONS[8];

  return PRICING_OPTIONS[7];
};

// Memoized Table Cell with custom dropdown UI matching screenshot
const ScheduleCell = React.memo(({ vendorId, day, slot, currentSlotVal, onCellChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const activeOption = useMemo(
    () => getMatchingOption(currentSlotVal?.pricingType, currentSlotVal?.showCoupon),
    [currentSlotVal]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectOption = (opt) => {
    onCellChange(vendorId, day, slot, {
      pricingType: opt.pricingType,
      showCoupon: opt.showCoupon,
    });
    setIsOpen(false);
  };

  return (
    <td
      style={{
        padding: "0",
        border: "1px solid #E2E8F0",
        width: "155px",
        minWidth: "155px",
        position: "relative",
        verticalAlign: "top",
      }}
      ref={dropdownRef}
    >
      {/* Trigger Button Card */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: activeOption.bg,
          color: activeOption.color,
          padding: "6px 8px",
          height: "42px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          transition: "all 0.15s ease-in-out",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: "1.1" }}>
          <span style={{ fontSize: "11px", fontWeight: "500", opacity: 0.9 }}>{activeOption.title}</span>
          {activeOption.subtitle ? (
            <span style={{ fontSize: "13px", fontWeight: "800", marginTop: "1px" }}>{activeOption.subtitle}</span>
          ) : null}
        </div>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke={activeOption.chevronColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            marginLeft: "4px",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {/* Custom Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 999,
            width: "175px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 12px 28px -4px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            borderRadius: "0 0 4px 4px",
            overflow: "hidden",
          }}
        >
          {PRICING_OPTIONS.map((opt) => (
            <div
              key={opt.key}
              onClick={() => handleSelectOption(opt)}
              style={{
                backgroundColor: opt.bg,
                color: opt.color,
                padding: "8px 10px",
                fontSize: "11px",
                fontWeight: "700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <span>{opt.title}</span>
              {opt.subtitle && <span style={{ fontWeight: "800" }}>{opt.subtitle}</span>}
            </div>
          ))}
        </div>
      )}
    </td>
  );
});

ScheduleCell.displayName = "ScheduleCell";

// Memoized Schedule Row with Vendor Name & Edit All dropdown feature
const ScheduleRow = React.memo(({ vendor, daysOfWeek, timeSlots, slotsData, onCellChange }) => {
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const batchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (batchRef.current && !batchRef.current.contains(event.target)) {
        setIsBatchOpen(false);
      }
    };
    if (isBatchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isBatchOpen]);

  const handleApplyToAllSlots = (opt) => {
    daysOfWeek.forEach((day) => {
      timeSlots.forEach((slot) => {
        onCellChange(vendor.vendorId, day, slot, {
          pricingType: opt.pricingType,
          showCoupon: opt.showCoupon,
        });
      });
    });
    setIsBatchOpen(false);
  };

  const defaultVendorPriceType = vendor?.pricingType || vendor?.currentPriceType;

  return (
    <tr>
      <td
        style={{
          padding: "10px 12px",
          borderRight: "1px solid #E2E8F0",
          borderBottom: "1px solid #E2E8F0",
          backgroundColor: "#FAFAFA",
          verticalAlign: "middle",
          position: "sticky",
          left: 0,
          zIndex: 10,
          width: "210px",
          minWidth: "210px",
        }}
        ref={batchRef}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#0F172A" }}>
              {vendor.vendorName}
            </div>
            <div style={{ fontSize: "11px", color: "#64748B", marginTop: "1px" }}>
              {defaultVendorPriceType ? `(${getMatchingOption(defaultVendorPriceType, vendor.showCoupon).title})` : "(Current price type)"}
            </div>
          </div>
          <button
            onClick={() => setIsBatchOpen(!isBatchOpen)}
            type="button"
            style={{
              background: "#FFFFFF",
              border: "1px solid #CBD5E1",
              borderRadius: "4px",
              padding: "2px 6px",
              fontSize: "10px",
              fontWeight: "600",
              color: "#334155",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "3px",
            }}
          >
            Edit All <span style={{ fontSize: "8px" }}>▲</span>
          </button>
        </div>

        {/* Batch Edit Dropdown overlay */}
        {isBatchOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "10px",
              zIndex: 1000,
              width: "185px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
              overflow: "hidden",
              border: "1px solid #E2E8F0",
            }}
          >
            <div style={{ padding: "6px 10px", fontSize: "10px", fontWeight: "700", color: "#64748B", backgroundColor: "#F1F5F9", textTransform: "uppercase" }}>
              Set All Slots To:
            </div>
            {PRICING_OPTIONS.map((opt) => (
              <div
                key={`batch-${opt.key}`}
                onClick={() => handleApplyToAllSlots(opt)}
                style={{
                  backgroundColor: opt.bg,
                  color: opt.color,
                  padding: "6px 10px",
                  fontSize: "11px",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <span>{opt.title}</span>
                {opt.subtitle && <span>{opt.subtitle}</span>}
              </div>
            ))}
          </div>
        )}
      </td>

      {daysOfWeek.map((day) =>
        timeSlots.map((slot) => {
          const currentSlotVal = slotsData?.[day]?.[slot] || (defaultVendorPriceType ? {
            pricingType: defaultVendorPriceType,
            showCoupon: vendor.showCoupon,
          } : {});
          return (
            <ScheduleCell
              key={`${vendor.vendorId}-${day}-${slot}`}
              vendorId={vendor.vendorId}
              day={day}
              slot={slot}
              currentSlotVal={currentSlotVal}
              onCellChange={onCellChange}
            />
          );
        })
      )}
    </tr>
  );
});

ScheduleRow.displayName = "ScheduleRow";

const VendorCoupons = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("selection"); // 'selection' or 'management'
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isMount, setIsMount] = useState(false);

  // Local modified schedule adjustments tracking
  const [localScheduleChanges, setLocalScheduleChanges] = useState({});

  // Edit states for coupon CRUD
  const [editDrawer, setEditDrawer] = useState({
    open: false,
    edit: "",
    title: "",
  });
  const [selectedCoupon, setSelectedCoupon] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  // Redux API for Vendor pricing schedule
  const { data: gridRes, isLoading: isGridLoading } = useGetVendorPricingScheduleQuery({
    page,
    limit: 50,
    search: searchQuery,
  });

  const [saveVendorPricingSchedule, { isLoading: isSavingSchedule }] = useSaveVendorPricingScheduleMutation();

  // Load Coupons
  const loadCoupons = useCallback(async () => {
    try {
      const couponsRes = await axios.get(`${apiURL}admin/coupons?limit=1000`);
      if (couponsRes?.data?.coupon) {
        setCoupons(couponsRes.data.coupon);
      }
    } catch (error) {
      console.error("Error loading coupons:", error);
    }
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons, isMount]);

  // Keep cell changes separate in local state before saving
  const handleCellChange = useCallback((vendorId, day, slot, newVal) => {
    setLocalScheduleChanges((prev) => {
      const vendorData = prev[vendorId] ? { ...prev[vendorId] } : {};
      const dayData = vendorData[day] ? { ...vendorData[day] } : {};
      dayData[slot] = newVal;
      vendorData[day] = dayData;
      return {
        ...prev,
        [vendorId]: vendorData,
      };
    });
  }, []);

  // Save changes for all modified vendors
  const saveSchedule = async () => {
    const vendorIdsToSave = Object.keys(localScheduleChanges);
    if (vendorIdsToSave.length === 0) {
      alert("No changes to save.");
      return;
    }

    try {
      setLoading(true);
      const items = gridRes?.data?.items || [];
      const daysOfWeek = gridRes?.data?.days || ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
      const timeSlots = gridRes?.data?.slotTimes || ["05:00", "11:00", "17:00"];

      for (const vendorId of vendorIdsToSave) {
        const vendorObj = items.find((v) => (v.vendorId || v._id) === vendorId);
        const serverSlots = vendorObj?.slots || {};
        const localChanges = localScheduleChanges[vendorId] || {};

        // Build complete merged slots object for all days and time slots
        const fullMergedSlots = {};

        daysOfWeek.forEach((day) => {
          fullMergedSlots[day] = {};
          timeSlots.forEach((slot) => {
            const localVal = localChanges?.[day]?.[slot];
            const serverVal = serverSlots?.[day]?.[slot];
            const defaultVal = (vendorObj?.pricingType || vendorObj?.currentPriceType) ? {
              pricingType: vendorObj.pricingType || vendorObj.currentPriceType,
              showCoupon: vendorObj.showCoupon,
            } : null;

            fullMergedSlots[day][slot] = localVal || serverVal || defaultVal || {
              pricingType: "smart",
              showCoupon: false,
            };
          });
        });

        await saveVendorPricingSchedule({
          vendorId,
          slots: fullMergedSlots,
        }).unwrap();
      }
      setLocalScheduleChanges({});
      alert("Pricing schedule updated successfully!");
    } catch (err) {
      console.error("Failed to save schedule:", err);
      alert("Error saving schedule!");
    } finally {
      setLoading(false);
    }
  };

  // Management tab helpers
  const handleAddNewCoupon = () => {
    setEditDrawer({
      open: true,
      edit: "AddNew",
      title: "Add New Coupon",
    });
    setSelectedCoupon({});
  };

  const handleEditCoupon = (item) => {
    setEditDrawer({
      open: true,
      edit: "update",
      title: "Update Coupon",
    });
    setSelectedCoupon(item);
  };

  const handleDeleteConfirm = async (id) => {
    setLoading(true);
    const res = await dispatch(deleteCouponAction(id));
    if (res) {
      setDeleteConfirm({ open: false, id: null });
      setIsMount(!isMount);
    }
    setLoading(false);
  };

  // Week schedule mapping
  const daysOfWeek = gridRes?.data?.days || ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const timeSlots = gridRes?.data?.slotTimes || ["05:00", "11:00", "17:00"];

  // Determine current day and slot for header badge calculation
  const currentDayName = useMemo(() => moment().format("dddd").toLowerCase(), []);
  const currentHour = useMemo(() => moment().hour(), []);

  const getIsCurrentSlot = (day, slot) => {
    if (day.toLowerCase() !== currentDayName) return false;
    // Check closest time slot (05:00, 11:00, 17:00)
    if (slot === "05:00" && currentHour >= 5 && currentHour < 11) return true;
    if (slot === "11:00" && currentHour >= 11 && currentHour < 17) return true;
    if (slot === "17:00" && (currentHour >= 17 || currentHour < 5)) return true;
    return false;
  };

  // Merge server slots with local unsaved changes for rendering
  const getMergedSlots = (vendorId, serverSlots) => {
    const localChanges = localScheduleChanges[vendorId] || {};
    const merged = { ...serverSlots };
    Object.keys(localChanges).forEach((day) => {
      merged[day] = {
        ...(merged[day] || {}),
        ...localChanges[day],
      };
    });
    return merged;
  };

  return (
    <div className="container-fluid" style={{ minHeight: "80vh", padding: "20px" }}>
      {(loading || isGridLoading) && <TopBarProgress />}

      {/* Header Bar */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ fontSize: "24px", fontWeight: "700", margin: 0, color: "#111827" }}>
            Vendor Pricing & Coupon Portal
          </h2>
          <p style={{ color: "#6B7280", margin: "4px 0 0 0", fontSize: "14px" }}>
            Manage weekly pricing schedules and coupon configurations.
          </p>
        </div>

        {/* Tab Buttons */}
        <div style={{ display: "flex", gap: "6px", background: "#E5E7EB", padding: "4px", borderRadius: "8px" }}>
          <button
            onClick={() => setActiveTab("selection")}
            style={{
              border: "none",
              background: activeTab === "selection" ? "#FFFFFF" : "transparent",
              color: activeTab === "selection" ? "#111827" : "#4B5563",
              padding: "8px 16px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: activeTab === "selection" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            }}
          >
            Weekly Grid Schedule
          </button>
          <button
            onClick={() => setActiveTab("management")}
            style={{
              border: "none",
              background: activeTab === "management" ? "#FFFFFF" : "transparent",
              color: activeTab === "management" ? "#111827" : "#4B5563",
              padding: "8px 16px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: activeTab === "management" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            }}
          >
            Coupon Config
          </button>
        </div>
      </div>

      {activeTab === "selection" ? (
        <div className="card shadow-sm p-4" style={{ borderRadius: "12px", border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF" }}>
          {/* Top Filter and Save Bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <SearchFilter
              placeholder="Search Vendor Name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              width="300px"
            />
            <CustomeTableBtn
              color="secondary"
              width="180px"
              onClick={saveSchedule}
              disabled={isSavingSchedule || Object.keys(localScheduleChanges).length === 0}
            >
              {isSavingSchedule ? "Saving..." : `Save Changes (${Object.keys(localScheduleChanges).length})`}
            </CustomeTableBtn>
          </div>

          {/* Grid Schedule Table matching reference design */}
          <div style={{ overflow: "auto", maxHeight: "75vh", border: "1px solid #E5E7EB", borderRadius: "8px" }}>
            <table className="table mb-0 align-middle" style={{ minWidth: "1600px", borderCollapse: "collapse" }}>
              <thead>
                {/* Upper Day Headers */}
                <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                  <th
                    style={{
                      width: "220px",
                      padding: "12px 16px",
                      fontWeight: "700",
                      fontSize: "14px",
                      color: "#111827",
                      backgroundColor: "#F9FAFB",
                      borderRight: "1px solid #E5E7EB",
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      height: "60px",
                    }}
                  >
                    Vendors
                    <div style={{ fontSize: "11px", fontWeight: "400", color: "#6B7280" }}>
                      Current price type
                    </div>
                  </th>
                  {daysOfWeek.map((day) => (
                    <th
                      key={day}
                      colSpan={timeSlots.length}
                      className="text-center"
                      style={{
                        padding: "10px",
                        fontWeight: "600",
                        fontSize: "15px",
                        color: "#111827",
                        backgroundColor: "#F9FAFB",
                        borderRight: "1px solid #E5E7EB",
                        borderLeft: "1px solid #E5E7EB",
                        textTransform: "capitalize",
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                        height: "60px",
                      }}
                    >
                      {day}
                    </th>
                  ))}
                </tr>

                {/* Sub Header for Time Slots */}
                <tr style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #E5E7EB" }}>
                  <th
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderRight: "1px solid #E5E7EB",
                      position: "sticky",
                      left: 0,
                      top: "60px",
                      zIndex: 30,
                    }}
                  ></th>
                  {daysOfWeek.map((day) =>
                    timeSlots.map((slot) => {
                      const isCurrent = getIsCurrentSlot(day, slot);
                      return (
                        <th
                          key={`${day}-${slot}`}
                          className="text-center"
                          style={{
                            padding: "6px 8px",
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isCurrent ? "#FFFFFF" : "#6B7280",
                            backgroundColor: isCurrent ? "#111827" : "#FFFFFF",
                            borderRight: "1px solid #E5E7EB",
                            borderLeft: "1px solid #E5E7EB",
                            position: "sticky",
                            top: "60px",
                            zIndex: 20,
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                            }}
                          >
                            <span>{slot}</span>
                            {isCurrent && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "800",
                                  fontStyle: "italic",
                                  color: "#FFFFFF",
                                }}
                              >
                                Current
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>
              <tbody>
                {gridRes?.data?.items?.map((vendor) => (
                  <ScheduleRow
                    key={vendor.vendorId}
                    vendor={vendor}
                    daysOfWeek={daysOfWeek}
                    timeSlots={timeSlots}
                    slotsData={getMergedSlots(vendor.vendorId, vendor.slots)}
                    onCellChange={handleCellChange}
                  />
                ))}
                {(!gridRes?.data?.items || gridRes?.data?.items?.length === 0) && (
                  <tr>
                    <td
                      colSpan={1 + daysOfWeek.length * timeSlots.length}
                      className="text-center py-5"
                      style={{ color: "#9CA3AF", fontSize: "14px" }}
                    >
                      No Vendors Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {gridRes?.data?.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span style={{ fontSize: "14px", color: "#6B7280" }}>
                Showing page {page} of {gridRes.data.totalPages}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page === gridRes.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card shadow-sm p-4" style={{ borderRadius: "12px", border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF" }}>
          {/* Controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 style={{ margin: 0, fontWeight: "600", color: "#374151" }}>Coupon Listing</h4>
            <CustomeTableBtn
              color="secondary"
              width="130px"
              onClick={handleAddNewCoupon}
            >
              Add Coupon
            </CustomeTableBtn>
          </div>

          <div className="dashboard-pages-table table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Sn#</th>
                  <th>Title</th>
                  <th>Price (%)</th>
                  <th>Coupon Code</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length > 0 ? (
                  coupons.map((item, i) => (
                    <tr key={item._id}>
                      <td>{i + 1}</td>
                      <td>{item.title}</td>
                      <td>{item.pricePercentage}%</td>
                      <td><code>{item.code}</code></td>
                      <td>{moment(item.from).format("MM/DD/YYYY")}</td>
                      <td>{moment(item.to).format("MM/DD/YYYY")}</td>
                      <td>{moment(item.createdAt).format("MM/DD/YYYY")}</td>
                      <td>
                        <CustomeTableBtn
                          color="secondary"
                          width="80px"
                          className="mr-2 btn-sm"
                          onClick={() => handleEditCoupon(item)}
                        >
                          Edit
                        </CustomeTableBtn>
                        <CustomeTableBtn
                          color="danger"
                          width="80px"
                          className="btn-sm"
                          onClick={() => setDeleteConfirm({ open: true, id: item._id })}
                        >
                          Delete
                        </CustomeTableBtn>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-4" style={{ color: "#9CA3AF" }}>
                      No Coupons Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slide drawer for create/edit coupon */}
      <EditTabelDrawer
        Edit={editDrawer}
        setEdit={setEditDrawer}
        shippingId={selectedCoupon?.title}
      >
        {editDrawer.edit === "AddNew" && (
          <AddNewCoupon
            isMount={isMount}
            setisMount={setIsMount}
            setEdit={setEditDrawer}
          />
        )}
        {editDrawer.edit === "update" && (
          <AddNewCoupon
            SelectItem={selectedCoupon}
            isMount={isMount}
            setisMount={setIsMount}
            setEdit={setEditDrawer}
          />
        )}
        <hr className="mb-0" />
      </EditTabelDrawer>

      {/* Delete Confirmation Alert */}
      {deleteConfirm.open && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Yes, delete it!"
          confirmBtnBsStyle="danger"
          title="Are you sure?"
          onConfirm={() => handleDeleteConfirm(deleteConfirm.id)}
          onCancel={() => setDeleteConfirm({ open: false, id: null })}
          focusCancelBtn
        >
          Are you sure you want to delete this coupon?
        </SweetAlert>
      )}
    </div>
  );
};

export default VendorCoupons;
